import {
  COMPRESS_ZEROS_THRESHOLD,
  compressZeros,
  formatPriceBase,
} from '@polaris/core/utils'
import { useMemo } from 'react'

function PriceWithCompressedZeros({
  price,
  formatOptions,
}: {
  price: number
  formatOptions?: Intl.NumberFormatOptions
}) {
  const { decimalDigits, significantDigits, zeros } = useMemo(
    () =>
      compressZeros(
        formatPriceBase(price, {
          maximumFractionDigits: 8,
          ...formatOptions,
        }),
        false,
      ),
    [formatOptions, price],
  )

  return (
    <>
      {significantDigits}.
      {zeros ? (
        <>
          0<sub title={`${price}`}>{zeros}</sub>
        </>
      ) : null}
      {decimalDigits?.slice(0, 2)}
    </>
  )
}

export function PriceDisplay({
  price,
  zeroValue = 'Price unknown',
  formatOptions,
}: {
  price: number | string | undefined
  zeroValue?: string
  formatOptions?: Intl.NumberFormatOptions
}) {
  const priceNumber = Number(
    typeof price === 'string' ? price.replace(/[$,]/g, '') : price,
  )

  if (Number.isNaN(priceNumber)) {
    return null
  }

  if (priceNumber === 0) {
    return zeroValue
  }

  if (1 <= priceNumber) {
    // Above $1 we show 2 decimal places
    return formatPriceBase(priceNumber, {
      roundingPriority: 'auto',
      ...formatOptions,
    })
  }

  if (priceNumber < COMPRESS_ZEROS_THRESHOLD) {
    return (
      <PriceWithCompressedZeros
        price={priceNumber}
        formatOptions={formatOptions}
      />
    )
  }

  return formatPriceBase(priceNumber, {
    maximumFractionDigits: 8,
    maximumSignificantDigits: 4,
    roundingPriority: 'auto',
    ...formatOptions,
  })
}
